import { createPortal } from "react-dom";
import { useNoteForm } from "@/v5/contexts/NoteFormContext";
import { NoteFormModal } from "@/v5/components/NoteFormModal";
import { Button } from "@/v5/components/ui/button";
import { cn } from "@/v5/lib/utils";
import { FileText, Maximize2, Mic, Square, X } from "lucide-react";
import { useAnyModalOpen } from "@/components/ui/Modal";
import { useIsBelow } from "@/hooks/useBreakpoint";

// Mounted once at the top of <Layout> alongside FloatingVoiceCall /
// FloatingVideoCall. Lifecycle mirrors those: while a note is open we keep the
// underlying NoteFormModal mounted in both modes so its sessionRef, transcript
// buffers, form state and timers survive route changes and minimize/maximize
// toggles. Only the wrapper visibility flips.
export function FloatingNoteForm() {
  const nf = useNoteForm();

  // Step aside for an open modal, on the phone tier only.
  //
  // This corner panel is z-[9999] and modals are z-50, and below `md` a modal is a
  // full-width sheet whose action row sits exactly where this box is — so it covers
  // the Save/Confirm button and the form cannot be completed. Modal.tsx names this
  // chrome explicitly as the reason its open-modal registry exists; the cookie banner
  // was simply the first subscriber.
  //
  // Hidden rather than unmounted, deliberately: an in-progress call or a dictation
  // session keeps its state, timers and refs, and comes straight back when the modal
  // closes. Gated below `md` so desktop output is untouched (R2) — there the modal is
  // centred and inset and the two do not fight for the same pixels.
  //
  // Both hooks are called on their own line, unconditionally, and only their results are
  // combined. `useAnyModalOpen() && useIsBelow("md")` reads as the same thing but is not:
  // `&&` short-circuits, so `useIsBelow` — a useSyncExternalStore hook — ran only while a
  // modal was open. Opening one therefore rendered one more hook than the previous render
  // and threw React #310, blanking the app shell on every page these widgets are mounted
  // on. Not phone-only either: the skipped call was gated on modal state, not the
  // viewport, so desktop crashed identically.
  const anyModalOpen = useAnyModalOpen();
  const isPhone = useIsBelow("md");
  const stepAsideForModal = anyModalOpen && isPhone;

  if (!nf.current) return null;

  const isMinimized = nf.mode === "minimized";

  // Portal to document.body so the note overlay sits in the same stacking
  // layer as other modals that use ReactDOM.createPortal (e.g. the appointment
  // `Modal` at /schedule). With both at z-50, DOM order decides — and because
  // this portal mounts when the note is opened (after any already-open modal),
  // the note correctly stacks above the modal that triggered it. Radix
  // Selects/Popovers used inside the note still portal later than us so they
  // continue to stack above the note. Voice/video calls live at z-[9999] and
  // remain above an open note as before. */
  return createPortal(
    <>
      {/* Expanded overlay. We always render the modal once a note is open;
          when minimized we just hide its overlay with `hidden` so display:none
          keeps the React subtree mounted and refs/state stay alive. */}
      <div
        className={cn(
          "fixed inset-0 z-50 grid place-items-center p-4",
          isMinimized ? "hidden" : "bg-black/60 backdrop-blur-sm",
        )}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) nf.minimize();
        }}
      >
        <NoteFormModal
          // Remount key mirrors openNote()'s sameNote rule: appointment context is
          // part of identity for a NEW note only. It can therefore only change after
          // the user accepted the discard confirm, so this adds no silent remount
          // (which would throw away editorRef / recording state — see L30-32).
          key={`${nf.current.patientId}-${
            nf.current.noteId ?? `new-${nf.current.linkAppointmentId ?? "x"}`
          }`}
          patientId={nf.current.patientId}
          noteId={nf.current.noteId}
          autoRecord={!!nf.current.autoRecord}
          linkAppointmentId={nf.current.linkAppointmentId}
          linkAppointmentLabel={nf.current.linkAppointmentLabel}
          onClose={nf.close}
          onMinimize={nf.minimize}
        />
      </div>

      {/* Minimized floating card. Sibling to the overlay so it isn't hidden
          when display:none-ing the overlay. */}
      {isMinimized && (
        <div className={cn("fixed bottom-6 right-6 z-[9999] w-[320px] rounded-2xl bg-card border border-border shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300", stepAsideForModal && "hidden")}>
          <button
            type="button"
            onClick={nf.expand}
            className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
            title="Maximize"
          >
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  {nf.draftSubject || (nf.current.noteId ? "Editing note" : "New note")}
                </p>
                {nf.current.patientName && (
                  <p className="text-[11px] text-muted-foreground truncate">
                    {nf.current.patientName}
                  </p>
                )}
              </div>
              {nf.isRecording && (
                <span className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[11px] text-red-500 font-medium">REC</span>
                </span>
              )}
            </div>
          </button>
          <div className="flex items-center gap-1 px-2 pb-2">
            {nf.isRecording ? (
              <Button
                variant="destructive"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={(e) => { e.stopPropagation(); nf.recordingControls?.stop(); }}
              >
                <Square className="h-3 w-3 fill-current" /> Stop
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={(e) => { e.stopPropagation(); nf.recordingControls?.start(); }}
              >
                <Mic className="h-3 w-3" /> Record
              </Button>
            )}
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="icon"
              onClick={nf.expand}
              className="h-7 w-7 rounded-full hover:bg-muted"
              title="Maximize"
              aria-label="Maximize note"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={nf.close}
              className="h-7 w-7 rounded-full hover:bg-red-100 hover:text-red-600"
              title="Close note"
              aria-label="Close note"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
